import { FC } from 'react'
import { ArrowRight } from 'lucide-react'
import CardInfoButton from './CardInfoButton'
import type { CardMeta } from './types'

const auditMeta: CardMeta = {
  description: 'Upcoming audits and their current status.',
  endpoint: 'GET /qhse/audits',
  logic: 'Shows audits sorted by auditFromDate. Open audits are those not finalized.',
}

const inspMeta: CardMeta = {
  description: 'Pending inspections across the fleet.',
  endpoint: 'GET /qhse/inspections',
  logic: 'Shows inspections sorted by inspectionFromDate. Pending = not finalized.',
}

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

interface Props {
  audits: any[]
  inspections: any[]
  onViewAudits: () => void
  onViewInspections: () => void
}

const AuditInspectionStatus: FC<Props> = ({ audits, inspections, onViewAudits, onViewInspections }) => {
  const recentAudits = audits.slice(0, 4)
  const recentInsp = inspections.slice(0, 4)

  return (
    <>
      {/* Upcoming Audits */}
      <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
        <div className='flex items-center justify-between mb-3'>
          <div className='flex items-center gap-2'>
            <h6 className='text-sm font-bold text-slate-900'>Upcoming Audits</h6>
            <CardInfoButton meta={auditMeta} />
          </div>
          <button onClick={onViewAudits} className='text-[10px] font-semibold text-blue-600 hover:text-blue-800 transition-colors'>
            View all <ArrowRight className='w-3 h-3 inline' />
          </button>
        </div>
        <div className='overflow-x-auto max-h-[220px] overflow-y-auto'>
          <table className='w-full text-[11px]'>
            <thead>
              <tr className='text-slate-400 text-[10px] uppercase tracking-wider'>
                <th className='text-left font-semibold py-2 pr-2'>Vessel</th>
                <th className='text-left font-semibold py-2 px-2'>Type</th>
                <th className='text-left font-semibold py-2 px-2'>Date</th>
                <th className='text-right font-semibold py-2 pl-2'>Status</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {recentAudits.length === 0 ? (
                <tr>
                  <td colSpan={4} className='text-center text-slate-400 py-8 text-xs'>No audits found</td>
                </tr>
              ) : (
                recentAudits.map((a, i) => (
                  <tr key={a.id ?? i} className='hover:bg-slate-50 transition-colors'>
                    <td className='py-2.5 pr-2 font-semibold text-slate-800'>{a.vesselName || '—'}</td>
                    <td className='py-2.5 px-2 text-slate-500'>{a.auditKindName || '—'}</td>
                    <td className='py-2.5 px-2 text-slate-700'>{fmtDate(a.auditFromDate)}</td>
                    <td className='py-2.5 pl-2 text-right'>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                        a.isFinalized ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                      }`}>{a.isFinalized ? 'Finalized' : 'Open'}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending Inspections */}
      <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
        <div className='flex items-center justify-between mb-3'>
          <div className='flex items-center gap-2'>
            <h6 className='text-sm font-bold text-slate-900'>Pending Inspections</h6>
            <CardInfoButton meta={inspMeta} />
          </div>
          <button onClick={onViewInspections} className='text-[10px] font-semibold text-blue-600 hover:text-blue-800 transition-colors'>
            View all <ArrowRight className='w-3 h-3 inline' />
          </button>
        </div>
        <div className='overflow-x-auto max-h-[220px] overflow-y-auto'>
          <table className='w-full text-[11px]'>
            <thead>
              <tr className='text-slate-400 text-[10px] uppercase tracking-wider'>
                <th className='text-left font-semibold py-2 pr-2'>Vessel</th>
                <th className='text-left font-semibold py-2 px-2'>Type</th>
                <th className='text-left font-semibold py-2 px-2'>Date</th>
                <th className='text-right font-semibold py-2 pl-2'>Status</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {recentInsp.length === 0 ? (
                <tr>
                  <td colSpan={4} className='text-center text-slate-400 py-8 text-xs'>No inspections found</td>
                </tr>
              ) : (
                recentInsp.map((i, idx) => (
                  <tr key={i.id ?? idx} className='hover:bg-slate-50 transition-colors'>
                    <td className='py-2.5 pr-2 font-semibold text-slate-800'>{i.vesselName || '—'}</td>
                    <td className='py-2.5 px-2 text-slate-500'>{i.inspectionKindName || '—'}</td>
                    <td className='py-2.5 px-2 text-slate-700'>{fmtDate(i.inspectionFromDate)}</td>
                    <td className='py-2.5 pl-2 text-right'>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                        i.isFinalized ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                      }`}>{i.isFinalized ? 'Finalized' : 'Open'}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

export default AuditInspectionStatus
